import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaCheckCircle, FaArrowLeft, FaDownload } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import html2pdf from 'html2pdf.js';
import './PaymentSuccess.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://localhost:5000";

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return '—';
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split('-').map(Number);
    return new Date(y, m - 1, day).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Extract only Hindi text for Donation Type
const getHindiDonationType = (type) => {
  if (!type) return '—';
  if (type.includes('प्रोत्साहन निधि') || type.toLowerCase().includes('encouragement')) return 'प्रोत्साहन निधि';
  if (type.includes('शिक्षा नेक') || type.toLowerCase().includes('education')) return 'शिक्षा नेक';
  if (type.includes('सामान्य दान') || type.toLowerCase().includes('general')) return 'सामान्य दान';
  
  const match = type.match(/\(([^)]+)\)/);
  if (match) return match[1];
  
  return type;
};

const PaymentSuccess = () => {
  const { txnNo } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const receiptRef = useRef();

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/payment/receipt/${txnNo}`);
        const result = await response.json();
        
        if (result.success) {
          setData(result);
        } else {
          setError(result.message || 'Receipt not found');
        }
      } catch (err) {
        setError('Failed to fetch receipt details.');
      } finally {
        setLoading(false);
      }
    };

    if (txnNo) fetchReceipt();
  }, [txnNo]);

  const handleDownloadPDF = () => {
    const element = receiptRef.current;
    if (!element) return;

    const clone = element.cloneNode(true);
    clone.style.cssText = 'width:794px;height:559px;aspect-ratio:unset;position:fixed;top:-9999px;left:-9999px;background:#fff;';
    clone.querySelectorAll('*').forEach(el => {
      el.style.fontFamily = 'Arial, sans-serif';
    });
    document.body.appendChild(clone);

    const opt = {
      margin: 0,
      filename: `Payment-Receipt_${data?.donation?.receiptNo || 'RSK'}.pdf`,
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 2, useCORS: true, allowTaint: true, letterRendering: true, width: 794, height: 559, logging: false },
      jsPDF: { unit: 'mm', format: 'a5', orientation: 'landscape' }
    };

    html2pdf().set(opt).from(clone).save().finally(() => document.body.removeChild(clone));
  };

  if (loading) return <div className="ps-status-msg">Loading Receipt...</div>;
  if (error) return (
    <div className="ps-status-msg error">
      <h2>Transaction Not Found</h2>
      <p>{error}</p>
      <Link to="/" className="ps-btn-back"><FaArrowLeft /> Back to Home</Link>
    </div>
  );

  const { donor, donation } = data;

  // Detect Online Logic & Formats
  const isOnline = donation.mode === 'Online Gateway' || donation.pgPaymentMode || (donation.receiptNo && donation.receiptNo.includes('ONL'));
  let transactionDetails = donation.refNo || donation.merchantTxnNo || '';
  if (transactionDetails === 'N/A') transactionDetails = '';
  let paymentModeDisplay = isOnline ? 'Online Gateway' : (donation.mode || 'CASH');

  return (
    <>
      <Helmet><title>Payment Success | Rajput Shiksha Kosh</title>

        <meta
          name="description"
          content="Payment success page for Rajput Shiksha Kosh, confirming successful transactions and providing options to download the receipt or return to the homepage."
        />
      </Helmet>
      
      <div className="ps-page-container">
        
        {/* Success Header (Hidden in PDF) */}
        <div className="ps-header-box">
          <FaCheckCircle className="ps-success-icon" />
          <h1>Payment Successful!</h1>
          <p>Thank you for your generous donation. Your transaction ID is <strong>{donation.merchantTxnNo}</strong>.</p>
          
          <div className="ps-actions">
            <button onClick={handleDownloadPDF} className="ps-btn-download">
              <FaDownload /> Download PDF
            </button>
            <Link to="/" className="ps-btn-home">
              <FaArrowLeft /> Return to Home
            </Link>
          </div>
        </div>

        {/* --- EXACT A5 HINDI RECEIPT TEMPLATE --- */}
        <div className="donation-receipt-wrapper">
          <div ref={receiptRef} className="donation-receipt-a5-container">
            <div className="rsk-receipt-border">
              
              {/* Top PAN and Reg No */}
              <div className="rsk-top-bar">
                <span>PAN - AADTR3478A</span>
                <span>रजि. नं. 05/2018/जोधपुर</span>
              </div>

              <hr className="rsk-hr" />

              {/* Main Huge Title */}
              <div className="rsk-main-title-block">
                <h1 className="rsk-title">राजपूत शिक्षा कोष</h1>
                <p className="rsk-subtitle">(प्रतिभावान जरूरतमंद बालक/बालिकाओं की शिक्षा व्यवस्था हेतु)</p>
              </div>

              <hr className="rsk-hr" />

              {/* Address and Tax Notice */}
              <div className="rsk-address-block">
                <p className="rsk-address">कार्यालय - मारवाड़ राजपूत सभा भवन, पावटा 'बी' रोड़, जोधपुर (राज.)</p>
                <p className="rsk-tax-note">(दान राशि पर आयकर एक्ट की धारा 80G के तहत छुट)</p>
              </div>

              {/* Receipt No and Date */}
              <div className="rsk-meta-row">
                <span className="rsk-meta-text">रसीद नं. <span className="rsk-dynamic-value">{donation.receiptNo || '—'}</span></span>
                <span className="rsk-meta-text">दिनांक <span className="rsk-dynamic-value">{fmtDate(donation.date)}</span></span>
              </div>

              {/* Donor Details Body in Hindi */}
              <div className="rsk-body">
                
                {/* Row 1: Name and Father's Name */}
                <div className="rsk-field-row">
                  <div className="rsk-field-half">
                    <span className="rsk-label">शुभ नाम :</span>
                    <span className="rsk-line rsk-bold">{donor.name || '—'}</span>
                  </div>
                  <div className="rsk-field-half">
                    <span className="rsk-label">पिता का नाम :</span>
                    <span className="rsk-line">{donor.fatherName || '—'}</span>
                  </div>
                </div>

                {/* Row 2: Contact and Donation Type */}
                <div className="rsk-field-row">
                  <div className="rsk-field-half">
                    <span className="rsk-label">मो.नं. :</span>
                    <span className="rsk-line">{donor.mobileNo || '—'}</span>
                  </div>
                  <div className="rsk-field-half">
                    <span className="rsk-label">बाबत :</span>
                    <span className="rsk-line rsk-bold">{getHindiDonationType(donation.donationType)}</span>
                  </div>
                </div>

                {/* Row 3: Payment Mode (Centered text on line) */}
                <div className="rsk-field-row" style={{ marginTop: '5px' }}>
                  <span className="rsk-label">मार्फत :</span>
                  <span className="rsk-line rsk-line-center">
                    {paymentModeDisplay} {transactionDetails ? `- ${transactionDetails}` : ''} 
                  </span>
                  <span className="rsk-label-suffix">सधन्यवाद प्राप्त हुए।</span>
                </div>

              </div>

              {/* Amount Box (Left) and 80G Info (Right Center) */}
              <div className="rsk-mid-section">
                <div className="rsk-amount-box">
                  ₹ {fmt(donation.amount)} /-
                </div>
                
                <div className="rsk-80g-box">
                  <p className="rsk-80g-title">Income Tax 80G की Authority</p>
                  <p className="rsk-80g-text">DIN NO. AADTR3478AF2021101 JAIPUR/80G/AY 2022-23 TO AY 2026-27</p>
                  <p className="rsk-80g-text">DATED: 06-04-2022, PAN: AADTR3478A</p>
                  <p className="rsk-80g-web">website: www.rajputshikshakosh.com | Email: info@rajputshikshakosh.com, rajputshikshakosh@gmail.com</p>
                </div>
              </div>

              <hr className="rsk-hr" />

              {/* Bank Details Section */}
              <div className="rsk-bank-section">
                <div className="rsk-bank-header">Bank Details</div>
                
                <div className="rsk-bank-grid">
                  {/* SBI Block */}
                  <div className="rsk-bank-col">
                    <div>Bank Name - <span className="rsk-bank-val">State Bank of India</span> A/C Name - <span className="rsk-bank-val">Rajput Shiksha Kosh</span></div>
                    <div>A/C No - <span className="rsk-bank-val">37561552700</span>, Branch - <span className="rsk-bank-val">Mandore Road, Jodhpur</span>, IFSC - <span className="rsk-bank-val">SBIN0031374</span></div>
                  </div>
                  
                  {/* ICICI Block */}
                  <div className="rsk-bank-col">
                    <div>Bank Name - <span className="rsk-bank-val">ICICI Bank</span> A/C Name - <span className="rsk-bank-val">Rajput Shiksha Kosh</span></div>
                    <div>A/C No. - <span className="rsk-bank-val">682801701715</span> Branch - <span className="rsk-bank-val">Paota, Jodhpur</span> IFSC - <span className="rsk-bank-val">ICIC0006828</span></div>
                  </div>
                </div>
              </div>

              {/* Centered System Generated Message */}
              <div className="rsk-footer-section">
                <div className="rsk-system-msg">
                  This is a system generated receipt hence no authorized signature required.
                </div>
              </div>

            </div>
          </div>
        </div>
        {/* --- END RECEIPT TEMPLATE --- */}

      </div>
    </>
  );
};

export default PaymentSuccess;